import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Client, Conversation, Message, Participant } from "@twilio/conversations";
import { Call, Device } from '@twilio/voice-sdk';
import { API } from "../io/Api";


async function initClient(twlToken: string): Promise<Client> {
    return new Promise((resolve, reject) => {
        const client = new Client(twlToken);
        client.on('initialized', () => {
            console.log('Client: initialized');
            resolve(client);
        });
        client.on('initFailed', (err) => {
            console.log('Client: init failed');
            reject(err.error);
        })
    })
}

export const useTwilioChat = (params: Record<string, string>) => {
    const [, setToken] = useState('');
    const [, setClient] = useState<Client>();
    const [chat, setChat] = useState<Conversation>();
    const [messages, setMessages] = useState<Message[]>([]);
    const clientReady = useRef(false);

    useEffect(() => {
        const init = async () => {
            const token = await API.getTwilioToken();
            const client = await initClient(token);
            setClient(client);

            client.on("tokenAboutToExpire", () => {
                console.log('Client: tokenAboutToExpire');
                API.getTwilioToken().then((token) => {
                    client.updateToken(token).then(() => setToken(token));
                });
            });

            const conv = await client.createConversation({ attributes: params });
            console.log('Chat: created');
            await conv.join();
            console.log('Chat: joined');
            setChat(conv);
            conv.on('participantJoined', (p: Participant) => {
                console.log('Chat: participantJoined', p);
            })
        };
        if (!clientReady.current) {
            clientReady.current = true;
            init().then(() => { }).catch(console.error);
        }
    }, [params]);

    useEffect(() => {
        chat?.removeAllListeners('messageAdded');
        chat?.on('messageAdded', (msg: Message) => {
            console.log('Chat: messageAdded', msg);
            setMessages([...messages, msg]);
        })
    }, [chat, messages])

    const sendMessage = useCallback(async (text: string) => {
        const i = await chat?.sendMessage(text, params);
        console.log('Chat: message was sent', i);
    }, [chat, params]);

    const resetChat = useCallback(() => { }, []);

    const joined = useMemo(() => !!chat, [chat]);

    return { messages, sendMessage, resetChat, joined };
}

export const useTwilioCall = (params: Record<string, string>) => {
    const [, setDevice] = useState<Device>();
    const [call, setCall] = useState<Call>();
    const [callState, setCallState] = useState<Call.State>();

    const dial = useCallback(async (to: string) => {
        const token = await API.getTwilioToken();

        // Create device after click to avoid auto-play policy warnings
        const device = new Device(token, {
            codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU]
        });
        setDevice(device);
        [
            "error", "incoming", "destroyed", "unregistered", "registering", "registered"
        ].forEach((evt) => {
            device.on(evt, (arg) => { console.log('Device: %s', evt, arg) });
        });
        device.on("tokenAboutToExpire", () => {
            API.getTwilioToken().then((token) => {
                device.updateToken(token)
                // setToken(token);
            });
        });

        await device?.register();

        const call = await device?.connect({ params: { To: to, ...params } });
        setCall(call);
        [
            'accept', 'audio', 'cancel', 'disconnect', 'error',
            'messageReceived', 'messageSent', 'mute', 'reconnected',
            'reconnecting', 'reject', 'ringing',
            // 'sample', 'volume',
            'warningCleared', 'warning'
        ].forEach((evt) => {
            call?.on(evt, (arg) => {
                console.log('Call: %s', evt, arg);
                setCallState(call.status());
            });
        });
    }, [params]);

    const hangup = useCallback(() => {
        call?.disconnect();
    }, [call]);

    const status = useMemo(() => (
        {
            closed: "",
            connecting: "new",
            open: "active",
            pending: "ringing",
            reconnecting: "ringing",
            ringing: "ringing"
        }[callState || 'closed'] || ''
    ), [callState]);

    return { dial, hangup, status };
}